import React, { useEffect, useState } from "react";
import { Container, Grid, Typography, Box, Card, Button } from "@mui/material";
import { Helmet } from "react-helmet";
import EmailLeads from "../components/dashboard/EmailLeads";
import UpcomingMeetings from "../components/dashboard/UpcomingMeetings";
import knowledgebaseAgentService from "../services/knowledgebase-agent.service";
import Psychology from "@mui/icons-material/Psychology";
import { useNavigate } from "react-router-dom";

const ActionsToFollow = () => {
  const [kbAgent, setKbAgent] = useState([]);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchKB = async () => {
      try {
        setLoading(true);
        const kb = await knowledgebaseAgentService.getAllKnowledgeBases();
        setKbAgent(kb);
      } catch (error) {
        console.error("Error loading KB agents:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchKB();
  }, []);

  return (
    <>
      <Helmet>
        <title>Actions to Follow | Sitepilot</title>
        <meta
          name="description"
          content="Follow up on email leads and manage scheduled meetings"
        />
      </Helmet>

      <Container
        component="main"
        maxWidth="lg"
        sx={{
          position: "relative",
          px: { xs: 2, sm: 3 },
          py: { xs: 3, sm: 4 },
        }}
      >
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              letterSpacing: "-0.02em",
              mb: 1.5,
              fontSize: { xs: "2rem", md: "2.5rem" },
            }}
          >
            Actions to Follow
          </Typography>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ fontWeight: 400, fontSize: { xs: "1rem", md: "1.25rem" } }}
          >
            Your business inbox — emails to follow up and meetings to attend
          </Typography>
        </Box>

        {/* --- SHOW EMPTY STATE IF NO KB AGENTS --- */}
        {loading ? null : kbAgent.length === 0 ? (
          <Box sx={{ maxWidth: 600, mx: "auto", mt: 6 }}>
            <Card
              sx={{
                textAlign: "center",
                p: 6,
                border: "2px dashed rgba(102, 126, 234, 0.3)",
                borderRadius: 4,
                background: "linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.9) 100%)",
                backdropFilter: "blur(20px)",
                boxShadow: "0 20px 40px -12px rgba(0, 0, 0, 0.15)",
                transition: "all 0.3s ease",
                "&:hover": {
                  borderColor: "rgba(102, 126, 234, 0.5)",
                  boxShadow: "0 24px 48px -12px rgba(102, 126, 234, 0.2)",
                  transform: "translateY(-4px)",
                },
              }}
            >
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: "50%",
                  background: "linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 2,
                  border: "2px solid rgba(102, 126, 234, 0.2)",
                }}
              >
                <Psychology
                  sx={{
                    fontSize: 40,
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}
                />
              </Box>
              <Typography
                variant="h5"
                sx={{
                  mb: 1,
                  fontWeight: 700,
                  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                No Agents Yet
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Complete onboarding to create your first AI chat agent
              </Typography>
              <Button
                variant="contained"
                onClick={() => navigate("/app/agent/add")}
                sx={{
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  boxShadow: "0 8px 24px rgba(102, 126, 234, 0.3)",
                  borderRadius: 3,
                  px: 4,
                  py: 1.5,
                  fontWeight: 600,
                  textTransform: "none",
                  "&:hover": {
                    background: "linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)",
                    boxShadow: "0 12px 32px rgba(102, 126, 234, 0.4)",
                    transform: "translateY(-2px)",
                  },
                }}
              >
                Start Onboarding
              </Button>
            </Card>
          </Box>
        ) : (
          <>
            {/* Email Leads and Meetings */}
            <Grid container spacing={3}>
              <Grid item xs={12} lg={6}>
                <EmailLeads />
              </Grid>
              <Grid item xs={12} lg={6}>
                <UpcomingMeetings />
              </Grid>
            </Grid>
          </>
        )}
      </Container>
    </>
  );
};

export default ActionsToFollow;
